import {createContext, useContext, useState, useEffect, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { TokenStorage, setLogoutHandler, setDobRequiredHandler } from '../services/api';
import * as authService from '../services/authService';
import { ANALYTICS_EVENTS, clearAnalyticsUser, setAnalyticsUser, trackEvent } from '../analytics';
import { setDiagnosticsUser } from '../diagnostics/onboardingDiagnostics';
import { clearFirstTouch } from '../attribution/firstTouch';
import { clearReferral, getReferral } from '../attribution/referral';
import { loadDraft } from '../utils/topMoviesDraft';
import { trackTopMovies } from '../analytics/topMoviesAnalytics';
import { loadRankingDraft } from '../utils/rankingDraft';
import { trackRanking } from '../analytics/rankingAnalytics';
import { readPendingQuiz, clearPendingQuiz } from '../quiz/pendingQuiz';
import { OnboardingService } from '../services/onboardingService';

/**
 * Where a freshly authenticated session should land, in priority order:
 *   1. DOB missing → /enter-dob (the caller's `from` is preserved in state)
 *   2. onboarding still needed (server-side rule: not completed AND no coin) → /onboarding
 *   3. a quiz the user was trying to start before the auth gate → that /verify path
 *   4. post-first-coin Watchlist step still pending → /onboarding/watchlist
 *   5. null → caller decides (usually its `from` or '/')
 */
export function resolvePostAuthDestination(payload, { pendingQuiz = null } = {}) {
    if (!payload) return null;
    if (payload.requiresDob) return '/enter-dob';
    if (payload.needsOnboarding ?? payload.user?.needsOnboarding) return '/onboarding';
    if (pendingQuiz?.path) return pendingQuiz.path;
    if (payload.needsWatchlistOnboarding ?? payload.user?.needsWatchlistOnboarding) return WATCHLIST_ONBOARDING_PATH;
    return null;
}

export const WATCHLIST_ONBOARDING_PATH = '/onboarding/watchlist';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

export function AuthProvider({ children }) {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [restoreState, setRestoreState] = useState({ visible: false, restoreToken: null });
    const isNewRegistration = useRef(false);
    const pendingMethod = useRef('email');

    useEffect(() => {
        // Rehydrate user from stored token on page load
        const stored = localStorage.getItem('aluvo_user');
        if (stored) {
            const restoredUser = JSON.parse(stored);
            setUser(restoredUser);
            setAnalyticsUser(restoredUser?.id || restoredUser?._id);
            setDiagnosticsUser(restoredUser?.id || restoredUser?._id);
        } else {
            trackEvent(ANALYTICS_EVENTS.GUEST_SESSION_STARTED, { platform: 'web' }, { onceKey: 'web-boot' });
        }
        setLoading(false);
    }, []);

    // Set up DOB required handler
    useEffect(() => {
        setDobRequiredHandler(() => {
            navigate('/enter-dob');
        });
    }, [navigate]);

    /**
     * Persist the session and return the destination the caller must navigate
     * to (or null to use its own default). Callers (Login/Register pages) must
     * honour a non-null destination — previously they navigated to `from`
     * afterwards and overrode the DOB / onboarding redirect.
     */
    const _handleAuthResult = useCallback((result, method = 'email') => {
        const payload = result.data || result;

        if (payload.accountDeleted) {
            setRestoreState({ visible: true, restoreToken: payload.restoreToken });
            return { destination: null, payload, deleted: true };
        }

        const storedUser = {
            ...payload.user,
            // Keep the activation flags on the stored user so a page refresh can
            // route correctly before /onboarding/status has answered.
            needsOnboarding: payload.needsOnboarding ?? payload.user?.needsOnboarding ?? false,
            onboardingCompleted: payload.onboardingCompleted ?? payload.user?.onboardingCompleted ?? false,
            emailVerified: payload.emailVerified ?? payload.user?.emailVerified ?? false,
            needsWatchlistOnboarding: payload.needsWatchlistOnboarding ?? payload.user?.needsWatchlistOnboarding ?? false,
        };

        TokenStorage.saveTokens(payload.tokens.accessToken, payload.tokens.refreshToken);
        localStorage.setItem('aluvo_user', JSON.stringify(storedUser));
        setUser(storedUser);
        setAnalyticsUser(storedUser?.id || storedUser?._id);
        setDiagnosticsUser(storedUser?.id || storedUser?._id);

        const isNewUser = payload.isNewUser === true || isNewRegistration.current;
        isNewRegistration.current = false;
        if (isNewUser) {
            // Same event for email, Google and Apple so the funnel can compare methods.
            trackEvent(ANALYTICS_EVENTS.SIGNUP_COMPLETED, { method, platform: 'web' }, { onceKey: `signup-${storedUser.id}` });
            trackEvent(ANALYTICS_EVENTS.GUEST_CONVERTED_TO_USER, { platform: 'web' }, { onceKey: `web-${storedUser?.id || 'unknown'}` });
            // Sign-ups that came through My Top Movies: their own guest ranking, or
            // a shared list they opened (the referral rode along in `attribution`).
            const referral = getReferral();
            if (loadDraft()?.pendingSave || referral?.ref_type === 'top_movies') {
                trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_SIGNUP_COMPLETED, { method, ...(referral?.ref_slug ? { ref: referral.ref_slug } : {}) }, { onceKey: storedUser.id });
            }
            // …and through Custom Rankings: a guest draft waiting to be saved, or a
            // shared ranking they opened.
            const rankingDraft = loadRankingDraft();
            if (rankingDraft?.pendingSave || referral?.ref_type === 'ranking') {
                const ref = referral?.ref_type === 'ranking' ? referral.ref_slug : rankingDraft?.sourceSlug;
                trackRanking(ANALYTICS_EVENTS.RANKING_SIGNUP_COMPLETED, { method, source: rankingDraft?.pendingSave ? 'editor' : 'shared_ranking', ...(ref ? { ref } : {}) }, { onceKey: storedUser.id });
            }
            clearFirstTouch(); // persisted server-side at signup; nothing more to send
            clearReferral();
        } else {
            trackEvent(ANALYTICS_EVENTS.LOGIN_COMPLETED, { method });
        }

        const destination = resolvePostAuthDestination(payload, { pendingQuiz: readPendingQuiz(storedUser.id) });
        if (destination) navigate(destination, { replace: true });
        return { destination, payload, isNewUser };
    }, [navigate]);

    const login = useCallback(async (credentials) => {
        const result = await authService.login(credentials);
        return _handleAuthResult(result, 'email');
    }, [_handleAuthResult]);

    const register = useCallback(async (fields) => {
        isNewRegistration.current = true;
        const result = await authService.register(fields);
        return _handleAuthResult(result, 'email');
    }, [_handleAuthResult]);

    const loginWithGoogle = useCallback(async (idToken) => {
        const result = await authService.googleLogin(idToken);
        return _handleAuthResult(result, 'google');
    }, [_handleAuthResult]);

    const loginWithApple = useCallback(async (applePayload) => {
        const result = await authService.appleLogin(applePayload);
        return _handleAuthResult(result, 'apple');
    }, [_handleAuthResult]);

    const logout = useCallback(async () => {
        const refreshToken = TokenStorage.getRefreshToken();
        await authService.logout(refreshToken).catch(() => {});
        TokenStorage.clearTokens();
        localStorage.removeItem('aluvo_user');
        // A quiz waiting on this account's verification is never resumed for another account.
        clearPendingQuiz();
        setUser(null);
        clearAnalyticsUser();
        setDiagnosticsUser(null);
        trackEvent(ANALYTICS_EVENTS.LOGOUT_COMPLETED, { method: 'session' });
    }, []);

    const restoreAccount = useCallback(async () => {
        const result = await authService.restoreAccount(restoreState.restoreToken);
        setRestoreState({ visible: false, restoreToken: null });
        return _handleAuthResult(result, 'restore');
    }, [restoreState.restoreToken, _handleAuthResult]);

    const updateUserData = useCallback(async (newUserData) => {
        const updatedUser = { ...user, ...newUserData };
        setUser(updatedUser);
        localStorage.setItem('aluvo_user', JSON.stringify(updatedUser));
    }, [user]);

    const submitDob = useCallback(async (dateOfBirth) => {
        try {
            const token = TokenStorage.getAccessToken();
            const response = await fetch(`${API_BASE_URL}/auth/dob`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ dateOfBirth }),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.message || 'Failed to save date of birth');
            }

            const data = await response.json();

            // Update user with DOB info
            const updatedUser = {
                ...user,
                dateOfBirth: data.data.dateOfBirth,
                dobProvided: data.data.dobProvided,
            };
            setUser(updatedUser);
            localStorage.setItem('aluvo_user', JSON.stringify(updatedUser));

            return { ...data, needsOnboarding: !!updatedUser.needsOnboarding };
        } catch (error) {
            console.error('DOB submission error:', error);
            throw error;
        }
    }, [user]);

    // Wire global logout (triggered by expired tokens in api.js)
    useEffect(() => {
        setLogoutHandler(logout);
    }, [logout]);

    /**
     * Re-sync activation flags from the server (called on session restore so a
     * completed onboarding persists across restarts and devices).
     */
    const refreshOnboardingStatus = useCallback(async () => {
        if (!TokenStorage.getAccessToken()) return null;
        try {
            const status = await authService.getOnboardingStatus();
            if (status) {
                setUser((prev) => {
                    if (!prev) return prev;
                    const next = { ...prev, needsOnboarding: status.needsOnboarding, onboardingCompleted: status.onboardingCompleted, emailVerified: status.emailVerified, coinCount: status.coinCount, needsWatchlistOnboarding: !!status.needsWatchlistOnboarding };
                    localStorage.setItem('aluvo_user', JSON.stringify(next));
                    return next;
                });
            }
            return status;
        } catch {
            return null;
        }
    }, []);

    /**
     * Finish the post-first-coin Watchlist step (Continue, Skip, or back). Local
     * state flips first so nothing holds the user on the page; the server call
     * is best-effort (on failure the step may be offered once more later).
     */
    const resolveWatchlistOnboarding = useCallback(async ({ skipped = false, itemsAddedCount = 0 } = {}) => {
        setUser((prev) => {
            if (!prev) return prev;
            const next = { ...prev, needsWatchlistOnboarding: false };
            localStorage.setItem('aluvo_user', JSON.stringify(next));
            return next;
        });
        try {
            await OnboardingService.completeWatchlistOnboarding({ skipped, itemsAddedCount });
        } catch (err) {
            console.warn('[Auth] completeWatchlistOnboarding failed:', err?.message);
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            register,
            updateUserData,
            refreshOnboardingStatus,
            resolveWatchlistOnboarding,
            loginWithGoogle,
            loginWithApple,
            logout,
            restoreState,
            restoreAccount,
            submitDob
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
